import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, X, Check, Trash2, Play, Music, Video, Image, 
  AlertCircle, RefreshCw, Film, Headphones, Lock, Eye, EyeOff,
  ChevronDown, Settings, Disc, Plus, Edit2, Pause, Library,
  FileAudio, FileVideo, LayoutGrid, List
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { AdminHeartbeat } from './AdminHeartbeat';
import { TrackManager } from './TrackManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 1 * 1024 * 1024; // 1MB chunks - MUST be under Kubernetes ingress limit

// Content types for general uploads
const CONTENT_TYPES = [
  { id: 'intro_video', label: 'Cinematic Intro Video', icon: Film, accept: 'video/*' },
  { id: 'concert_audio', label: '3D Concert Audio Track', icon: Headphones, accept: 'audio/*' },
  { id: 'music_video', label: 'Music Video', icon: Video, accept: 'video/*' },
  { id: 'track_audio', label: 'Track Audio', icon: Music, accept: 'audio/*' },
  { id: 'gallery_image', label: 'Gallery Image', icon: Image, accept: 'image/*' },
  { id: 'garage_video', label: 'Garage Band Video (Raw)', icon: FileVideo, accept: 'video/*' }
];

// Music genres
const GENRES = ['Electronic', 'Hip Hop', 'Rock', 'Ambient', 'Cinematic', 'Pop', 'R&B', 'Jazz', 'Classical', 'World', 'Funk', 'Soul', 'Alternative', 'Experimental'];
const MOODS = ['Energetic', 'Chill', 'Dark', 'Uplifting', 'Dramatic', 'Happy', 'Sad', 'Mysterious', 'Romantic', 'Intense', 'Dreamy', 'Aggressive'];

export const AdminPanel = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState('general'); // 'general', 'music', 'garage'
  
  // General upload state
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [selectedContentType, setSelectedContentType] = useState('intro_video');
  const [title, setTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);
  
  // Music Library state
  const [musicFiles, setMusicFiles] = useState([]);
  const [musicLoading, setMusicLoading] = useState(false);
  const [songMetadata, setSongMetadata] = useState({
    title: '',
    artist: 'THE REDACTED',
    album: '',
    genre: 'Electronic',
    bpm: '',
    key: '',
    mood: [],
    description: ''
  });
  const [selectedMusicFile, setSelectedMusicFile] = useState(null);
  const [selectedCoverArt, setSelectedCoverArt] = useState(null);
  const musicFileRef = useRef(null);
  const coverArtRef = useRef(null);
  
  // Garage Videos state
  const [garageVideos, setGarageVideos] = useState([]);
  const [selectedGarageFile, setSelectedGarageFile] = useState(null);
  const [garageTitle, setGarageTitle] = useState('');
  const [garageDescription, setGarageDescription] = useState('');
  const garageFileRef = useRef(null);
  
  // Bulk upload queue
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);

  // Check stored token
  useEffect(() => {
    const storedToken = localStorage.getItem('mindesign_admin_token');
    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch media when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchMedia();
      fetchMusicLibrary();
      fetchGarageVideos();
    }
  }, [isAuthenticated]);

  const handleLogin = async () => {
    setAuthError('');
    
    // Simple token validation - if it matches the expected format, let them in
    // Backend will reject unauthorized requests for write operations
    if (token && token.length > 10) {
      localStorage.setItem('mindesign_admin_token', token);
      setIsAuthenticated(true);
    } else {
      setAuthError('Please enter a valid admin token');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('mindesign_admin_token');
    setToken('');
    setIsAuthenticated(false);
    setMedia([]);
  };

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/uploads/media`);
      const data = await response.json();
      setMedia(data.media || []);
    } catch (err) {
      setError('Failed to fetch media');
    } finally {
      setLoading(false);
    }
  };

  const fetchMusicLibrary = async () => {
    setMusicLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/music/songs?limit=100`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setMusicFiles(data.songs || []);
    } catch (err) {
      console.error('Failed to fetch music library:', err);
    } finally {
      setMusicLoading(false);
    }
  };

  const fetchGarageVideos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/uploads/media`);
      const data = await response.json();
      const videos = (data.media || []).filter(m => m.content_type === 'garage_video');
      setGarageVideos(videos);
    } catch (err) {
      console.error('Failed to fetch garage videos:', err);
    }
  };

  // Chunked upload function - handles ANY file size up to 100MB+
  const uploadChunked = async (file, contentType, fileTitle, onProgress) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    
    // Initialize upload
    onProgress?.(`Initializing ${fileSizeMB}MB upload...`, 0);
    
    let initResponse;
    try {
      initResponse = await fetch(`${API_URL}/api/uploads/init-chunk`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: new URLSearchParams({
          filename: file.name,
          file_size: file.size.toString(),
          file_type: file.type.split('/')[0] || 'unknown',
          content_type: contentType,
          title: fileTitle || file.name
        })
      });
    } catch (e) {
      throw new Error('Network error initializing upload. Please try again.');
    }

    if (!initResponse.ok) {
      const errText = await initResponse.text();
      throw new Error(`Failed to initialize upload: ${errText}`);
    }

    const { upload_id } = await initResponse.json();

    // Upload chunks with retry logic
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const chunkSizeKB = Math.round((end - start) / 1024);

      const formData = new FormData();
      formData.append('chunk_index', i.toString());
      formData.append('total_chunks', totalChunks.toString());
      formData.append('chunk', chunk, `chunk_${i}`);

      const progressPercent = Math.round(((i + 1) / totalChunks) * 90);
      onProgress?.(`Uploading ${fileSizeMB}MB: chunk ${i + 1}/${totalChunks} (${chunkSizeKB}KB)`, progressPercent);

      // Retry logic - try up to 3 times per chunk
      let chunkResponse;
      let lastError;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          chunkResponse = await fetch(`${API_URL}/api/uploads/chunk/${upload_id}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
          
          if (chunkResponse.ok) {
            break; // Success, move to next chunk
          }
          lastError = `HTTP ${chunkResponse.status}`;
        } catch (e) {
          lastError = e.message;
        }
        
        if (attempt < 3) {
          onProgress?.(`Chunk ${i + 1} failed, retrying (${attempt}/3)...`, progressPercent);
          await new Promise(r => setTimeout(r, 1000 * attempt)); // Exponential backoff
        }
      }

      if (!chunkResponse?.ok) {
        throw new Error(`Failed to upload chunk ${i + 1}: ${lastError}`);
      }
    }

    // Complete upload
    onProgress?.('Assembling file...', 95);

    const completeResponse = await fetch(`${API_URL}/api/uploads/complete/${upload_id}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!completeResponse.ok) {
      const errText = await completeResponse.text();
      throw new Error(`Failed to complete upload: ${errText}`);
    }

    onProgress?.('Complete!', 100);
    return await completeResponse.json();
  };

  // Simple upload for small files
  const uploadSimple = async (file, contentType, fileTitle) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('content_type', contentType);
    formData.append('title', fileTitle || file.name);

    const response = await fetch(`${API_URL}/api/uploads/simple`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return await response.json();
  };

  // Smart upload - auto-selects chunked for large files
  const smartUpload = async (file, contentType, fileTitle, onProgress) => {
    // ALWAYS use chunked for files > 1MB to bypass ingress limits
    // This ensures files up to 100MB+ work correctly
    if (file.size > 1 * 1024 * 1024) {
      return await uploadChunked(file, contentType, fileTitle, onProgress);
    } else {
      onProgress?.('Uploading...', 50);
      const result = await uploadSimple(file, contentType, fileTitle);
      onProgress?.('Complete!', 100);
      return result;
    }
  };

  // General upload handler
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');
    setSuccess('');

    try {
      const result = await smartUpload(
        selectedFile, 
        selectedContentType, 
        title,
        (status, progress) => {
          setUploadStatus(status);
          setUploadProgress(progress);
        }
      );

      setSuccess(`Successfully uploaded: ${result.url || result.file?.url}`);
      setSelectedFile(null);
      setTitle('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchMedia();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadStatus('');
    }
  };

  // Music Library upload handler
  const handleMusicUpload = async () => {
    if (!selectedMusicFile) {
      setError('Please select an audio file');
      return;
    }

    if (!songMetadata.title) {
      setError('Please enter a song title');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');
    setSuccess('');

    try {
      // First upload the audio file
      const audioResult = await smartUpload(
        selectedMusicFile,
        'music_library',
        songMetadata.title,
        (status, progress) => {
          setUploadStatus(status);
          setUploadProgress(progress * 0.7); // 70% for audio
        }
      );

      let coverArtUrl = null;
      
      // Upload cover art if provided
      if (selectedCoverArt) {
        setUploadStatus('Uploading cover art...');
        const coverResult = await smartUpload(
          selectedCoverArt,
          'cover_art',
          `${songMetadata.title}_cover`,
          (status, progress) => {
            setUploadStatus(status);
            setUploadProgress(70 + progress * 0.2); // 20% for cover
          }
        );
        coverArtUrl = `${API_URL}/api/uploads/files/${coverResult.url?.split('/').pop() || coverResult.file?.filename}`;
      }

      // Create song record in database
      setUploadStatus('Creating song record...');
      setUploadProgress(95);
      
      const audioUrl = `${API_URL}/api/uploads/files/${audioResult.url?.split('/').pop() || audioResult.file?.filename}`;
      
      // Get duration from filename or default
      const durationStr = songMetadata.duration || '3:30';
      const durationParts = durationStr.split(':');
      const durationSeconds = parseInt(durationParts[0]) * 60 + parseInt(durationParts[1] || 0);

      const songData = {
        title: songMetadata.title,
        artist: songMetadata.artist || 'THE REDACTED',
        album: songMetadata.album || null,
        duration: durationStr,
        duration_seconds: durationSeconds,
        genre: songMetadata.genre || 'Electronic',
        bpm: songMetadata.bpm ? parseInt(songMetadata.bpm) : null,
        key: songMetadata.key || null,
        mood: songMetadata.mood || [],
        tags: [],
        description: songMetadata.description || null,
        full_audio_url: audioUrl,
        cover_art_url: coverArtUrl,
        is_exclusive: false,
        featured: false
      };

      const createResponse = await fetch(`${API_URL}/api/music/songs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(songData)
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create song record');
      }

      setUploadProgress(100);
      setSuccess(`Successfully added "${songMetadata.title}" to Music Library!`);
      
      // Reset form
      setSelectedMusicFile(null);
      setSelectedCoverArt(null);
      setSongMetadata({
        title: '',
        artist: 'THE REDACTED',
        album: '',
        genre: 'Electronic',
        bpm: '',
        key: '',
        mood: [],
        description: ''
      });
      if (musicFileRef.current) musicFileRef.current.value = '';
      if (coverArtRef.current) coverArtRef.current.value = '';
      
      fetchMusicLibrary();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadStatus('');
    }
  };

  // Garage Video upload handler
  const handleGarageUpload = async () => {
    if (!selectedGarageFile) {
      setError('Please select a video file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');
    setSuccess('');

    try {
      const result = await smartUpload(
        selectedGarageFile,
        'garage_video',
        garageTitle || selectedGarageFile.name,
        (status, progress) => {
          setUploadStatus(status);
          setUploadProgress(progress);
        }
      );

      setSuccess(`Successfully uploaded garage band video!`);
      setSelectedGarageFile(null);
      setGarageTitle('');
      setGarageDescription('');
      if (garageFileRef.current) garageFileRef.current.value = '';
      
      fetchGarageVideos();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadStatus('');
    }
  };

  // Bulk Music Upload - add files to queue
  const handleBulkMusicSelect = (e) => {
    const files = Array.from(e.target.files);
    const audioFiles = files.filter(f => f.type.startsWith('audio/'));
    
    const newQueue = audioFiles.map(file => ({
      file,
      title: file.name.replace(/\.[^/.]+$/, '').replace(/_/g, ' '),
      artist: 'THE REDACTED',
      genre: 'Electronic',
      status: 'pending',
      progress: 0
    }));
    
    setUploadQueue(prev => [...prev, ...newQueue]);
  };

  // Process bulk upload queue
  const processBulkQueue = async () => {
    if (isProcessingQueue || uploadQueue.length === 0) return;
    
    setIsProcessingQueue(true);
    
    for (let i = 0; i < uploadQueue.length; i++) {
      const item = uploadQueue[i];
      if (item.status !== 'pending') continue;

      // Update status
      setUploadQueue(prev => prev.map((q, idx) => 
        idx === i ? { ...q, status: 'uploading' } : q
      ));

      try {
        // Upload audio
        const audioResult = await smartUpload(
          item.file,
          'music_library',
          item.title,
          (status, progress) => {
            setUploadQueue(prev => prev.map((q, idx) => 
              idx === i ? { ...q, progress } : q
            ));
          }
        );

        // Create song record
        const audioUrl = `${API_URL}/api/uploads/files/${audioResult.url?.split('/').pop() || audioResult.file?.filename}`;
        
        await fetch(`${API_URL}/api/music/songs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            title: item.title,
            artist: item.artist,
            album: null,
            duration: '3:30',
            duration_seconds: 210,
            genre: item.genre,
            mood: [],
            tags: [],
            full_audio_url: audioUrl,
            is_exclusive: false,
            featured: false
          })
        });

        setUploadQueue(prev => prev.map((q, idx) => 
          idx === i ? { ...q, status: 'complete', progress: 100 } : q
        ));
      } catch (err) {
        setUploadQueue(prev => prev.map((q, idx) => 
          idx === i ? { ...q, status: 'error', error: err.message } : q
        ));
      }
    }

    setIsProcessingQueue(false);
    fetchMusicLibrary();
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const response = await fetch(`${API_URL}/api/uploads/media/${mediaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('File deleted');
        fetchMedia();
      } else {
        setError('Failed to delete file');
      }
    } catch (err) {
      setError('Delete failed');
    }
  };

  const handleDeleteSong = async (songId) => {
    if (!window.confirm('Are you sure you want to delete this song?')) return;

    try {
      const response = await fetch(`${API_URL}/api/music/songs/${songId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Song deleted');
        fetchMusicLibrary();
      } else {
        setError('Failed to delete song');
      }
    } catch (err) {
      setError('Delete failed');
    }
  };

  const handleActivate = async (mediaId) => {
    try {
      const response = await fetch(`${API_URL}/api/uploads/media/${mediaId}/activate`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Media activated');
        fetchMedia();
      } else {
        setError('Failed to activate');
      }
    } catch (err) {
      setError('Activation failed');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const toggleMood = (mood) => {
    setSongMetadata(prev => ({
      ...prev,
      mood: prev.mood.includes(mood) 
        ? prev.mood.filter(m => m !== mood)
        : [...prev.mood, mood]
    }));
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gunmetal-950 flex items-center justify-center p-4">
        <div className="bg-gunmetal-900 rounded-2xl p-8 w-full max-w-md border border-steel-700/30">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-accent-red/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-accent-red" />
            </div>
            <h1 className="text-2xl font-black text-white">Admin Panel</h1>
            <p className="text-steel-400 mt-2">MINDESIGN ENTERPRISES</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-steel-300 text-sm mb-2">Admin Token</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  className="w-full bg-gunmetal-950 border border-steel-700 rounded-lg px-4 py-3 text-white focus:border-accent-red focus:outline-none"
                  placeholder="Enter admin token"
                  data-testid="admin-token-input"
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-steel-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {authError && (
              <div className="flex items-center space-x-2 text-red-400 text-sm">
                <AlertCircle size={16} />
                <span>{authError}</span>
              </div>
            )}

            <Button
              onClick={handleLogin}
              className="w-full bg-accent-red hover:bg-red-600 text-white py-3"
              data-testid="admin-login-btn"
            >
              Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main Admin Panel
  return (
    <div className="min-h-screen bg-gunmetal-950 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-black text-white">Content Manager</h1>
            <p className="text-steel-400">Upload and manage your media empire</p>
          </div>
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => { fetchMedia(); fetchMusicLibrary(); fetchGarageVideos(); }}
              variant="ghost"
              className="text-steel-400 hover:text-white"
            >
              <RefreshCw size={20} className={loading || musicLoading ? 'animate-spin' : ''} />
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-steel-400 hover:text-red-400"
            >
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-gunmetal-900 rounded-xl p-4 border border-steel-700/30">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-accent-red/20 rounded-xl flex items-center justify-center">
                <Music className="text-accent-red" size={24} />
              </div>
              <div>
                <p className="text-2xl font-black text-white">{musicFiles.length}</p>
                <p className="text-steel-400 text-sm">Songs in Library</p>
              </div>
            </div>
          </div>
          <div className="bg-gunmetal-900 rounded-xl p-4 border border-steel-700/30">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Video className="text-purple-400" size={24} />
              </div>
              <div>
                <p className="text-2xl font-black text-white">{garageVideos.length}</p>
                <p className="text-steel-400 text-sm">Garage Band Videos</p>
              </div>
            </div>
          </div>
          <div className="bg-gunmetal-900 rounded-xl p-4 border border-steel-700/30">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                <Library className="text-cyan-400" size={24} />
              </div>
              <div>
                <p className="text-2xl font-black text-white">{media.length}</p>
                <p className="text-steel-400 text-sm">Total Media Files</p>
              </div>
            </div>
          </div>
        </div>

        {/* MINDESIGN Heartbeat — live ecosystem pulse */}
        <AdminHeartbeat adminToken={token} />

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 border-b border-steel-700/30 pb-4">
          <button
            onClick={() => setActiveTab('music')}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'music' 
                ? 'bg-accent-red text-white' 
                : 'bg-gunmetal-800 text-steel-400 hover:text-white'
            }`}
            data-testid="tab-music"
          >
            <Disc size={20} />
            <span>Music Library (500+ Songs)</span>
          </button>
          <button
            onClick={() => setActiveTab('curate')}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'curate'
                ? 'bg-amber-500 text-gunmetal-950'
                : 'bg-gunmetal-800 text-steel-400 hover:text-white'
            }`}
            data-testid="tab-curate"
          >
            <Trash2 size={20} />
            <span>Curate Tracks</span>
          </button>
          <button
            onClick={() => setActiveTab('garage')}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'garage' 
                ? 'bg-purple-600 text-white' 
                : 'bg-gunmetal-800 text-steel-400 hover:text-white'
            }`}
            data-testid="tab-garage"
          >
            <FileVideo size={20} />
            <span>Garage Band Videos</span>
          </button>
          <button
            onClick={() => setActiveTab('general')}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'general' 
                ? 'bg-cyan-600 text-white' 
                : 'bg-gunmetal-800 text-steel-400 hover:text-white'
            }`}
            data-testid="tab-general"
          >
            <Upload size={20} />
            <span>General Uploads</span>
          </button>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-6 flex items-center space-x-2 text-red-400 bg-red-400/10 p-4 rounded-xl border border-red-400/30">
            <AlertCircle size={20} />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}
        {success && (
          <div className="mb-6 flex items-center space-x-2 text-green-400 bg-green-400/10 p-4 rounded-xl border border-green-400/30">
            <Check size={20} />
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}

        {/* MUSIC LIBRARY TAB */}
        {activeTab === 'music' && (
          <div className="space-y-8">
            {/* Single Song Upload */}
            <div className="bg-gunmetal-900 rounded-2xl p-6 border border-steel-700/30">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <Plus className="mr-2 text-accent-red" size={24} />
                Add Song to Music Library
              </h2>

              <div className="grid lg:grid-cols-2 gap-8">
                {/* Left: File Selection */}
                <div className="space-y-4">
                  {/* Audio File */}
                  <div>
                    <label className="block text-steel-300 text-sm mb-2">Audio File *</label>
                    <div
                      onClick={() => musicFileRef.current?.click()}
                      className="border-2 border-dashed border-steel-700 rounded-xl p-6 text-center cursor-pointer hover:border-accent-red transition-colors"
                    >
                      {selectedMusicFile ? (
                        <div>
                          <FileAudio className="w-10 h-10 text-accent-red mx-auto mb-2" />
                          <p className="text-white font-bold truncate">{selectedMusicFile.name}</p>
                          <p className="text-steel-400 text-sm">{formatFileSize(selectedMusicFile.size)}</p>
                        </div>
                      ) : (
                        <div>
                          <FileAudio className="w-10 h-10 text-steel-500 mx-auto mb-2" />
                          <p className="text-steel-400">Click to select audio file</p>
                          <p className="text-steel-500 text-xs mt-1">MP3, WAV, FLAC, M4A - Any size</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={musicFileRef}
                      type="file"
                      accept="audio/*"
                      onChange={(e) => {
                        setSelectedMusicFile(e.target.files[0]);
                        if (!songMetadata.title && e.target.files[0]) {
                          setSongMetadata(prev => ({
                            ...prev,
                            title: e.target.files[0].name.replace(/\.[^/.]+$/, '').replace(/_/g, ' ')
                          }));
                        }
                      }}
                      className="hidden"
                    />
                  </div>

                  {/* Cover Art */}
                  <div>
                    <label className="block text-steel-300 text-sm mb-2">Cover Art (optional)</label>
                    <div
                      onClick={() => coverArtRef.current?.click()}
                      className="border-2 border-dashed border-steel-700 rounded-xl p-4 text-center cursor-pointer hover:border-purple-500 transition-colors"
                    >
                      {selectedCoverArt ? (
                        <div className="flex items-center space-x-4">
                          <img 
                            src={URL.createObjectURL(selectedCoverArt)} 
                            alt="Cover" 
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                          <div className="text-left">
                            <p className="text-white font-medium truncate">{selectedCoverArt.name}</p>
                            <p className="text-steel-400 text-sm">{formatFileSize(selectedCoverArt.size)}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-3">
                          <Image className="w-8 h-8 text-steel-500" />
                          <span className="text-steel-400">Add cover art</span>
                        </div>
                      )}
                    </div>
                    <input
                      ref={coverArtRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => setSelectedCoverArt(e.target.files[0])}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* Right: Metadata */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-steel-300 text-sm mb-2">Title *</label>
                      <Input
                        value={songMetadata.title}
                        onChange={(e) => setSongMetadata(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Song title"
                        className="bg-gunmetal-950 border-steel-700"
                      />
                    </div>
                    <div>
                      <label className="block text-steel-300 text-sm mb-2">Artist</label>
                      <Input
                        value={songMetadata.artist}
                        onChange={(e) => setSongMetadata(prev => ({ ...prev, artist: e.target.value }))}
                        placeholder="THE REDACTED"
                        className="bg-gunmetal-950 border-steel-700"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-steel-300 text-sm mb-2">Genre</label>
                      <select
                        value={songMetadata.genre}
                        onChange={(e) => setSongMetadata(prev => ({ ...prev, genre: e.target.value }))}
                        className="w-full bg-gunmetal-950 border border-steel-700 rounded-lg px-3 py-2 text-white"
                      >
                        {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-steel-300 text-sm mb-2">BPM</label>
                      <Input
                        type="number"
                        value={songMetadata.bpm}
                        onChange={(e) => setSongMetadata(prev => ({ ...prev, bpm: e.target.value }))}
                        placeholder="120"
                        className="bg-gunmetal-950 border-steel-700"
                      />
                    </div>
                    <div>
                      <label className="block text-steel-300 text-sm mb-2">Key</label>
                      <Input
                        value={songMetadata.key}
                        onChange={(e) => setSongMetadata(prev => ({ ...prev, key: e.target.value }))}
                        placeholder="C Major"
                        className="bg-gunmetal-950 border-steel-700"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-steel-300 text-sm mb-2">Mood (select multiple)</label>
                    <div className="flex flex-wrap gap-2">
                      {MOODS.map(mood => (
                        <button
                          key={mood}
                          onClick={() => toggleMood(mood)}
                          className={`px-3 py-1 rounded-full text-sm transition-all ${
                            songMetadata.mood.includes(mood)
                              ? 'bg-accent-red text-white'
                              : 'bg-gunmetal-700 text-steel-400 hover:bg-gunmetal-600'
                          }`}
                        >
                          {mood}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-steel-300 text-sm mb-2">Description</label>
                    <textarea
                      value={songMetadata.description}
                      onChange={(e) => setSongMetadata(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Optional description..."
                      className="w-full bg-gunmetal-950 border border-steel-700 rounded-lg px-4 py-2 text-white h-20 resize-none"
                    />
                  </div>

                  <Button
                    onClick={handleMusicUpload}
                    disabled={!selectedMusicFile || !songMetadata.title || uploading}
                    className="w-full bg-accent-red hover:bg-red-600 text-white py-3 disabled:opacity-50"
                    data-testid="upload-song-btn"
                  >
                    {uploading ? 'Uploading...' : 'Add to Music Library'}
                  </Button>
                </div>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="mt-6 bg-gunmetal-950 rounded-xl p-4">
                  <div className="flex justify-between text-sm text-steel-400 mb-2">
                    <span>{uploadStatus}</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="h-3 bg-gunmetal-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-accent-red to-orange-500 transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Bulk Upload */}
            <div className="bg-gunmetal-900 rounded-2xl p-6 border border-steel-700/30">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                <Upload className="mr-2 text-orange-500" size={24} />
                Bulk Upload (Multiple Songs)
              </h2>
              <p className="text-steel-400 mb-4">Select multiple audio files to upload at once. Metadata will be auto-generated from filenames.</p>
              
              <div className="flex items-center space-x-4 mb-4">
                <input
                  type="file"
                  accept="audio/*"
                  multiple
                  onChange={handleBulkMusicSelect}
                  className="hidden"
                  id="bulk-music-input"
                />
                <label
                  htmlFor="bulk-music-input"
                  className="px-6 py-3 bg-gunmetal-700 hover:bg-gunmetal-600 text-white rounded-xl cursor-pointer transition-colors flex items-center space-x-2"
                >
                  <Plus size={20} />
                  <span>Add Files to Queue</span>
                </label>
                
                {uploadQueue.length > 0 && (
                  <Button
                    onClick={processBulkQueue}
                    disabled={isProcessingQueue}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    {isProcessingQueue ? 'Processing...' : `Upload ${uploadQueue.filter(q => q.status === 'pending').length} Songs`}
                  </Button>
                )}
              </div>

              {/* Upload Queue */}
              {uploadQueue.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {uploadQueue.map((item, idx) => (
                    <div key={item.id || item.filename || `upl-${idx}`} className="flex items-center space-x-4 bg-gunmetal-800 rounded-lg p-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        item.status === 'complete' ? 'bg-green-500/20 text-green-400' :
                        item.status === 'error' ? 'bg-red-500/20 text-red-400' :
                        item.status === 'uploading' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-steel-700 text-steel-400'
                      }`}>
                        {item.status === 'complete' ? <Check size={16} /> :
                         item.status === 'error' ? <X size={16} /> :
                         item.status === 'uploading' ? <RefreshCw size={16} className="animate-spin" /> :
                         <Music size={16} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{item.title}</p>
                        <p className="text-steel-500 text-sm">{formatFileSize(item.file.size)}</p>
                      </div>
                      {item.status === 'uploading' && (
                        <div className="w-24">
                          <div className="h-2 bg-gunmetal-700 rounded-full overflow-hidden">
                            <div className="h-full bg-orange-500" style={{ width: `${item.progress}%` }} />
                          </div>
                        </div>
                      )}
                      {item.status === 'pending' && (
                        <button
                          onClick={() => setUploadQueue(prev => prev.filter((_, i) => i !== idx))}
                          className="text-steel-500 hover:text-red-400"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Music Library List */}
            <div className="bg-gunmetal-900 rounded-2xl p-6 border border-steel-700/30">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <Disc className="mr-2 text-accent-red" size={24} />
                  Music Library ({musicFiles.length} songs)
                </h2>
              </div>

              {musicLoading ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 text-accent-red mx-auto animate-spin" />
                  <p className="text-steel-400 mt-2">Loading music library...</p>
                </div>
              ) : musicFiles.length === 0 ? (
                <div className="text-center py-12">
                  <Music className="w-16 h-16 text-steel-600 mx-auto mb-4" />
                  <p className="text-steel-400">No songs in library yet</p>
                  <p className="text-steel-500 text-sm">Upload your first song above!</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {musicFiles.map((song) => (
                    <div key={song.id} className="flex items-center space-x-4 bg-gunmetal-800/50 hover:bg-gunmetal-800 rounded-xl p-4 transition-colors">
                      <div className="w-12 h-12 bg-gunmetal-700 rounded-lg overflow-hidden flex-shrink-0">
                        {song.cover_art_url ? (
                          <img src={song.cover_art_url} alt={song.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent-red/20 to-purple-500/20">
                            <Disc className="text-accent-red" size={20} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{song.title}</p>
                        <p className="text-steel-400 text-sm truncate">{song.artist} • {song.genre}</p>
                      </div>
                      <div className="text-steel-500 text-sm">{song.duration || '3:30'}</div>
                      <button
                        onClick={() => handleDeleteSong(song.id)}
                        className="text-steel-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* CURATE TRACKS TAB */}
        {activeTab === 'curate' && (
          <TrackManager adminToken={token} />
        )}

        {/* GARAGE BAND VIDEOS TAB */}
        {activeTab === 'garage' && (
          <div className="space-y-8">
            {/* Upload */}
            <div className="bg-gunmetal-900 rounded-2xl p-6 border border-purple-500/30">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <FileVideo className="mr-2 text-purple-400" size={24} />
                Upload Garage Band Video
              </h2>
              <p className="text-steel-400 mb-6">Upload raw, unedited garage band footage of THE REDACTED. Any file size supported!</p>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <div
                    onClick={() => garageFileRef.current?.click()}
                    className="border-2 border-dashed border-purple-500/50 rounded-xl p-8 text-center cursor-pointer hover:border-purple-400 transition-colors"
                  >
                    {selectedGarageFile ? (
                      <div>
                        <Video className="w-12 h-12 text-purple-400 mx-auto mb-2" />
                        <p className="text-white font-bold truncate">{selectedGarageFile.name}</p>
                        <p className="text-steel-400">{formatFileSize(selectedGarageFile.size)}</p>
                      </div>
                    ) : (
                      <div>
                        <Video className="w-12 h-12 text-steel-500 mx-auto mb-2" />
                        <p className="text-steel-400">Click to select video</p>
                        <p className="text-steel-500 text-sm mt-1">MP4, MOV, AVI, WebM - Any size</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={garageFileRef}
                    type="file"
                    accept="video/*"
                    onChange={(e) => {
                      setSelectedGarageFile(e.target.files[0]);
                      if (!garageTitle && e.target.files[0]) {
                        setGarageTitle(e.target.files[0].name.replace(/\.[^/.]+$/, ''));
                      }
                    }}
                    className="hidden"
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-steel-300 text-sm mb-2">Title</label>
                    <Input
                      value={garageTitle}
                      onChange={(e) => setGarageTitle(e.target.value)}
                      placeholder="e.g., Garage Session #1 - The Grime"
                      className="bg-gunmetal-950 border-steel-700"
                    />
                  </div>
                  <div>
                    <label className="block text-steel-300 text-sm mb-2">Description (optional)</label>
                    <textarea
                      value={garageDescription}
                      onChange={(e) => setGarageDescription(e.target.value)}
                      placeholder="Raw footage from the early days..."
                      className="w-full bg-gunmetal-950 border border-steel-700 rounded-lg px-4 py-2 text-white h-20 resize-none"
                    />
                  </div>
                  <Button
                    onClick={handleGarageUpload}
                    disabled={!selectedGarageFile || uploading}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 disabled:opacity-50"
                    data-testid="upload-garage-btn"
                  >
                    {uploading ? 'Uploading...' : 'Upload Garage Video'}
                  </Button>
                </div>
              </div>

              {/* Upload Progress */}
              {uploading && activeTab === 'garage' && (
                <div className="mt-6 bg-gunmetal-950 rounded-xl p-4">
                  <div className="flex justify-between text-sm text-steel-400 mb-2">
                    <span>{uploadStatus}</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="h-3 bg-gunmetal-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Video List */}
            <div className="bg-gunmetal-900 rounded-2xl p-6 border border-steel-700/30">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <Video className="mr-2 text-purple-400" size={24} />
                Garage Band Videos ({garageVideos.length})
              </h2>

              {garageVideos.length === 0 ? (
                <div className="text-center py-12">
                  <Video className="w-16 h-16 text-steel-600 mx-auto mb-4" />
                  <p className="text-steel-400">No garage videos uploaded yet</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {garageVideos.map((video) => (
                    <div key={video.id} className="bg-gunmetal-800 rounded-xl overflow-hidden">
                      <div className="aspect-video bg-gunmetal-950 flex items-center justify-center">
                        <video
                          src={`${API_URL}/api/uploads/files/${video.filename}`}
                          className="w-full h-full object-cover"
                          controls
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-white font-semibold truncate">{video.title}</h4>
                        <p className="text-steel-500 text-sm">{formatFileSize(video.file_size)}</p>
                        <button
                          onClick={() => handleDelete(video.id)}
                          className="mt-2 text-red-400 hover:text-red-300 text-sm flex items-center"
                        >
                          <Trash2 size={14} className="mr-1" /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* GENERAL UPLOADS TAB */}
        {activeTab === 'general' && (
          <div className="space-y-8">
            {/* Upload Section */}
            <div className="bg-gunmetal-900 rounded-2xl p-6 border border-steel-700/30">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                <Upload className="mr-2 text-cyan-400" size={24} />
                Upload Content
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Content Type Selection */}
                <div>
                  <label className="block text-steel-300 text-sm mb-2">Content Type</label>
                  <div className="grid grid-cols-1 gap-2">
                    {CONTENT_TYPES.filter(t => t.id !== 'garage_video').map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          onClick={() => setSelectedContentType(type.id)}
                          className={`flex items-center space-x-3 p-3 rounded-lg border transition-all ${
                            selectedContentType === type.id
                              ? 'border-cyan-500 bg-cyan-500/10 text-white'
                              : 'border-steel-700 text-steel-400 hover:border-steel-500'
                          }`}
                        >
                          <Icon size={20} />
                          <span>{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* File Selection */}
                <div>
                  <label className="block text-steel-300 text-sm mb-2">Select File</label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-steel-700 rounded-xl p-8 text-center cursor-pointer hover:border-cyan-500 transition-colors"
                  >
                    {selectedFile ? (
                      <div>
                        <Check className="w-12 h-12 text-green-400 mx-auto mb-2" />
                        <p className="text-white font-bold truncate">{selectedFile.name}</p>
                        <p className="text-steel-400">{formatFileSize(selectedFile.size)}</p>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-12 h-12 text-steel-500 mx-auto mb-2" />
                        <p className="text-steel-400">Click to select file</p>
                        <p className="text-steel-500 text-sm mt-1">Any file size supported</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={CONTENT_TYPES.find(t => t.id === selectedContentType)?.accept}
                    onChange={(e) => {
                      setSelectedFile(e.target.files[0]);
                      if (!title && e.target.files[0]) {
                        setTitle(e.target.files[0].name.replace(/\.[^/.]+$/, ''));
                      }
                    }}
                    className="hidden"
                  />

                  {/* Title Input */}
                  <div className="mt-4">
                    <label className="block text-steel-300 text-sm mb-2">Title (optional)</label>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter title"
                      className="bg-gunmetal-950 border-steel-700"
                    />
                  </div>

                  {/* Upload Button */}
                  <Button
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                    className="w-full mt-4 bg-cyan-600 hover:bg-cyan-700 text-white py-3 disabled:opacity-50"
                    data-testid="upload-general-btn"
                  >
                    {uploading ? 'Uploading...' : 'Upload File'}
                  </Button>

                  {/* Upload Progress */}
                  {uploading && activeTab === 'general' && (
                    <div className="mt-4">
                      <div className="flex justify-between text-sm text-steel-400 mb-1">
                        <span>{uploadStatus}</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="h-2 bg-gunmetal-950 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-cyan-500 transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Media Library */}
            <div className="bg-gunmetal-900 rounded-2xl p-6 border border-steel-700/30">
              <h2 className="text-xl font-bold text-white mb-4">Media Library</h2>

              {loading ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 text-cyan-500 mx-auto animate-spin" />
                  <p className="text-steel-400 mt-2">Loading media...</p>
                </div>
              ) : media.length === 0 ? (
                <div className="text-center py-12">
                  <Library className="w-16 h-16 text-steel-600 mx-auto mb-4" />
                  <p className="text-steel-400">No media uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {CONTENT_TYPES.map((type) => {
                    const typeMedia = media.filter(m => m.content_type === type.id);
                    if (typeMedia.length === 0) return null;
                    
                    const Icon = type.icon;
                    return (
                      <div key={type.id} className="border border-steel-700/30 rounded-xl overflow-hidden">
                        <div className="bg-gunmetal-950 px-4 py-3 flex items-center space-x-2">
                          <Icon size={18} className="text-cyan-400" />
                          <span className="font-bold text-white">{type.label}</span>
                          <span className="text-steel-500 text-sm">({typeMedia.length})</span>
                        </div>
                        <div className="divide-y divide-steel-800">
                          {typeMedia.map((item) => (
                            <div key={item.id} className="p-4 flex items-center justify-between hover:bg-gunmetal-950/50">
                              <div className="flex items-center space-x-4">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  item.active ? 'bg-green-500/20 text-green-400' : 'bg-steel-700/50 text-steel-400'
                                }`}>
                                  <Icon size={20} />
                                </div>
                                <div>
                                  <p className="text-white font-medium">{item.title}</p>
                                  <p className="text-steel-500 text-sm">
                                    {formatFileSize(item.file_size)} • {item.original_name}
                                  </p>
                                </div>
                                {item.active && (
                                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                                    ACTIVE
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                {(type.id === 'intro_video' || type.id === 'concert_audio') && !item.active && (
                                  <Button
                                    onClick={() => handleActivate(item.id)}
                                    size="sm"
                                    variant="ghost"
                                    className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-400/10"
                                  >
                                    <Play size={16} className="mr-1" /> Activate
                                  </Button>
                                )}
                                <Button
                                  onClick={() => handleDelete(item.id)}
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-8 bg-gunmetal-900/50 rounded-xl p-6 border border-steel-700/20">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center">
            <Settings className="mr-2" size={20} />
            Upload Guide
          </h3>
          <div className="grid md:grid-cols-3 gap-6 text-steel-400 text-sm">
            <div>
              <h4 className="text-accent-red font-bold mb-2">Music Library</h4>
              <p>Upload your 250+ songs with metadata. Files are automatically chunked for any size. Add cover art, BPM, mood tags, and more.</p>
            </div>
            <div>
              <h4 className="text-purple-400 font-bold mb-2">Garage Band Videos</h4>
              <p>Raw, unedited footage of THE REDACTED. No size limit - our chunked upload handles massive video files.</p>
            </div>
            <div>
              <h4 className="text-cyan-400 font-bold mb-2">General Media</h4>
              <p>Intro videos, concert audio, music videos, and gallery images. Activate content to use it across the site.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
